/**
 * Which map camera moves are the member's own, and how long a card-driven
 * recenter takes.
 *
 * Mapbox fires `moveend` for every camera change. Only a move the member made
 * should start a new search (GitHub #95). Mapbox tags a genuine gesture with a
 * real `originalEvent`, tags a resize-driven move with an `originalEvent` of
 * type 'resize', 'orientationchange', 'fullscreenchange' or
 * 'webkitfullscreenchange' (every non-gesture camera change it can attribute
 * to a DOM event comes through `Map._onWindowResize`, which listens on exactly
 * these), and gives programmatic moves (`flyTo`, `easeTo`, `fitBounds`,
 * `jumpTo` with no event data) no `originalEvent` at all, the same test
 * mapbox-gl's own GeolocateControl uses. react-map-gl hands the raw mapbox
 * event to `onMoveEnd`, so the field is intact there.
 *
 * That is why a recenter driven by the map card's arrows or swipe cannot start
 * a search or clear the selection: it goes through `easeTo` with no event data.
 * A member who grabs the map mid-ease cancels the ease and produces a real
 * gesture event, which does search, as it should.
 */
const NON_GESTURE_MOVE_TYPES = new Set([
  'resize',
  'orientationchange',
  'fullscreenchange',
  'webkitfullscreenchange',
]);

/** True only for a camera move caused by the member's own pan/zoom/drag. */
export function isMemberGestureMove(originalEvent: { type?: string } | null | undefined): boolean {
  if (!originalEvent) return false;
  return !NON_GESTURE_MOVE_TYPES.has(originalEvent.type ?? '');
}

/** Duration of the short ease when the map follows the selected card. */
export const PAN_TO_DURATION_MS = 350;

/** Duration when the same move also zooms (a cluster expanding to reveal the pin). */
export const PAN_ZOOM_DURATION_MS = 600;

/**
 * The ease duration for a card-driven recenter: instant under reduced motion,
 * a little longer when it also zooms.
 */
export function getPanToDuration(prefersReducedMotion: boolean, zooming = false): number {
  if (prefersReducedMotion) return 0;
  return zooming ? PAN_ZOOM_DURATION_MS : PAN_TO_DURATION_MS;
}
